'use client';

import classnames from 'classnames';
import React, { useState } from 'react';

import PreviewImages from '@/app/[locale]/(default-layout)/info/_components/PreviewImages';

import VideoPlayerModal from '@/components/video/VideoPlayerModal';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';

import { IconPlayButton96 } from '@cineamo/ui/src/icons/ic-playButton';
import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const DefaultCinemaImage = loadAsset('/images/placeholders/im-cinemaScreenPlaceholder.png');

type HeaderImagesProps = {
    /** Maximum of 2 images will be displayed */
    primaryImages?: string[];
    /** Maximum of 4 images will be displayed */
    secondaryImages?: string[];
    /** Total number of images across the whole gallery (building + all cinema rooms). Drives the "show all" button. */
    totalImagesCount?: number;
    /** When set, the first primary image shows a play button that opens this video. */
    primaryVideoUrl?: string;
    /**
     * Called with the index of the clicked image within the full gallery (primaryImages first, then
     * secondaryImages), so the caller can open the fullscreen gallery at the right position.
     */
    onImageClick?: (index: number) => void;
};

export default function HeaderImages(props: HeaderImagesProps): React.JSX.Element {
    const domainConfiguration = useDomainConfiguration();
    const cinema = domainConfiguration.cinema;
    const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
    const primaryImages = props.primaryImages?.length ? props.primaryImages.slice(0, 2) : [DefaultCinemaImage];
    return (
        <div className="relative w-full h-[25vw] min-h-[234px] flex flex-row space-x-4 md:space-x-12 rounded-8 overflow-hidden">
            <div
                className={classnames(
                    'w-0 flex-auto h-full overflow-hidden bg-[var(--surface-color)] flex flex-row space-x-4 md:space-x-12'
                )}>
                {primaryImages.map((src, index) => (
                    <div
                        key={index}
                        className={classnames('relative w-full h-full overflow-hidden', {
                            'cursor-pointer clickable-element': !!props.onImageClick
                        })}
                        onClick={props.onImageClick ? () => props.onImageClick?.(index) : undefined}>
                        <ImageWithTransition
                            className="w-full h-full object-cover"
                            src={src}
                            alt={cinema.name}
                            width={1000}
                            height={450}
                            priority
                        />
                        {index === 0 && !!props.primaryVideoUrl && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <IconPlayButton96
                                    className="pointer-events-auto clickable-element"
                                    foregroundcolor="white"
                                    fill="black"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setIsPlayerModalOpen(true);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <PreviewImages
                domainConfiguration={domainConfiguration}
                className="flex flex-none h-full"
                cinema={cinema}
                images={props.secondaryImages ?? []}
                totalImagesCount={props.totalImagesCount}
                onImageClick={
                    props.onImageClick ? (index) => props.onImageClick?.(primaryImages.length + index) : undefined
                }
                onShowAllImagesButtonClick={props.onImageClick ? () => props.onImageClick?.(0) : undefined}
            />

            {!!props.primaryVideoUrl && (
                <VideoPlayerModal
                    url={props.primaryVideoUrl}
                    isOpen={isPlayerModalOpen}
                    setIsOpen={setIsPlayerModalOpen}
                />
            )}
        </div>
    );
}
